import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Check, X, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/stores/auth/auth-store";
import { useBillingStore } from "@/stores/billing/billing-store";
import {
  TIER_DISPLAY,
  TIER_FEATURES,
  type BillingInterval,
  type SubscriptionTier,
} from "@/types/billing";

/**
 * PricingPage
 * Public page showing tier comparison with pricing, features, and CTAs.
 * Supports monthly/annual toggle with "2 months free" badge for annual billing.
 */

const TIERS_ORDER: SubscriptionTier[] = ["free", "basic", "standard", "pro", "enterprise"];

type TierFeatureKey = keyof typeof TIER_FEATURES[SubscriptionTier];

const FEATURE_LABELS: { key: TierFeatureKey; label: string }[] = [
  { key: "vaults", label: "Token Vaults" },
  { key: "workflow", label: "Workflow Engine" },
  { key: "dataService", label: "Data Service" },
  { key: "collaboration", label: "Real-time Collaboration" },
  { key: "multipleAdmins", label: "Multiple Admins" },
];

const getRegisterPath = (tier: SubscriptionTier, billingInterval: BillingInterval) => {
  const params = new URLSearchParams({
    selectedTier: tier,
  });

  if (tier !== "free") {
    params.set("billingInterval", billingInterval);
  }

  return `/register?${params.toString()}`;
};

export const PricingPage: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const { isAuthenticated } = useAuth();
  const { createCheckoutSession, isLoading } = useBillingStore();
  const navigate = useNavigate();

  const handleSubscribe = async (tier: SubscriptionTier) => {
    const billingInterval = isAnnual ? "annual" : "monthly";

    if (tier === "free") {
      if (isAuthenticated) {
        navigate("/dashboard");
      } else {
        navigate(getRegisterPath(tier, billingInterval));
      }
      return;
    }

    if (tier === "enterprise") {
      navigate("/contact");
      return;
    }

    if (!isAuthenticated) {
      navigate(getRegisterPath(tier, billingInterval));
      return;
    }

    try {
      await createCheckoutSession(tier, billingInterval);
    } catch {
      // Error is handled in the store
    }
  };

  const getCtaLabel = (tier: SubscriptionTier): string => {
    if (tier === "free") return isAuthenticated ? "Current Plan" : "Sign Up Free";
    if (tier === "enterprise") return "Contact Sales";
    return isAuthenticated ? "Subscribe" : "Start with this plan";
  };

  const getCtaAccessibleLabel = (tier: SubscriptionTier): string => {
    const planName = TIER_DISPLAY[tier].name;
    const billingInterval = isAnnual ? "annual" : "monthly";

    if (tier === "free") {
      return isAuthenticated
        ? `Current ${planName} plan`
        : `Sign up for ${planName} plan`;
    }

    if (tier === "enterprise") {
      return `Contact sales for ${planName} plan`;
    }

    return isAuthenticated
      ? `Subscribe to ${planName} ${billingInterval} plan`
      : `Start with ${planName} ${billingInterval} plan`;
  };

  const getPrice = (tier: SubscriptionTier): string => {
    const display = TIER_DISPLAY[tier];
    if (tier === "free") return "$0";
    if (tier === "enterprise") return "Custom";
    const price = isAnnual ? display.annualPrice / 12 : display.monthlyPrice;
    // For annual, show monthly equivalent; round to whole number if no decimal
    return `$${price % 1 === 0 ? price : price.toFixed(2)}`;
  };

  const getBillingLabel = (tier: SubscriptionTier): string => {
    if (tier === "free" || tier === "enterprise") return "";
    if (isAnnual) {
      const total = TIER_DISPLAY[tier].annualPrice;
      return `$${total}/year (billed annually)`;
    }
    return "/month";
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Simple, transparent pricing
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Choose the plan that fits your team. Upgrade or downgrade anytime.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 flex items-center justify-center gap-3">
            <span className={`text-sm font-medium ${!isAnnual ? "text-foreground" : "text-muted-foreground"}`}>
              Monthly
            </span>
            <Switch
              checked={isAnnual}
              onCheckedChange={setIsAnnual}
              aria-label="Toggle annual billing"
            />
            <span className={`text-sm font-medium ${isAnnual ? "text-foreground" : "text-muted-foreground"}`}>
              Annual
            </span>
            {isAnnual && (
              <Badge className="ml-2 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                2 months free
              </Badge>
            )}
          </div>
        </div>

        {/* Pricing cards */}
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {TIERS_ORDER.map((tier) => {
            const display = TIER_DISPLAY[tier];
            const features = TIER_FEATURES[tier];
            const isPopular = tier === "standard";

            return (
              <Card
                key={tier}
                className={`relative flex flex-col ${
                  isPopular
                    ? "border-2 border-primary shadow-lg ring-1 ring-primary/20"
                    : "border-border"
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="gap-1 bg-primary text-primary-foreground">
                      <Sparkles className="h-3 w-3" />
                      Most Popular
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center">
                  <CardTitle className="text-lg">{display.name}</CardTitle>
                  <CardDescription className="text-sm">
                    {display.description}
                  </CardDescription>

                  <div className="mt-4">
                    <span className="text-4xl font-bold text-foreground">
                      {getPrice(tier)}
                    </span>
                    {tier !== "free" && tier !== "enterprise" && (
                      <span className="text-sm text-muted-foreground">
                        {isAnnual ? "/mo" : "/mo"}
                      </span>
                    )}
                  </div>
                  {getBillingLabel(tier) && tier !== "free" && tier !== "enterprise" && !isAnnual && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {getBillingLabel(tier)}
                    </p>
                  )}
                  {isAnnual && tier !== "free" && tier !== "enterprise" && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {getBillingLabel(tier)}
                    </p>
                  )}
                </CardHeader>

                <CardContent className="flex flex-1 flex-col">
                  {/* Feature list */}
                  <ul className="flex-1 space-y-3">
                    <li className="flex items-center justify-between border-b border-border pb-3 text-sm">
                      <span className="font-semibold text-foreground">Token Vaults</span>
                      <span className="text-lg font-bold text-primary">
                        {features.vaults === '0' ? '—' : features.vaults}
                      </span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground">
                      {features.workflow ? (
                        <Check className="h-4 w-4 flex-shrink-0 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 flex-shrink-0 text-muted-foreground/40" />
                      )}
                      <span className={!features.workflow ? "text-muted-foreground/60" : ""}>
                        Workflow Engine
                      </span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground">
                      {features.dataService ? (
                        <Check className="h-4 w-4 flex-shrink-0 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 flex-shrink-0 text-muted-foreground/40" />
                      )}
                      <span className={!features.dataService ? "text-muted-foreground/60" : ""}>
                        Data Service
                      </span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground">
                      {features.collaboration ? (
                        <Check className="h-4 w-4 flex-shrink-0 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 flex-shrink-0 text-muted-foreground/40" />
                      )}
                      <span className={!features.collaboration ? "text-muted-foreground/60" : ""}>
                        Real-time Collaboration
                      </span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground">
                      {features.multipleAdmins ? (
                        <Check className="h-4 w-4 flex-shrink-0 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 flex-shrink-0 text-muted-foreground/40" />
                      )}
                      <span className={!features.multipleAdmins ? "text-muted-foreground/60" : ""}>
                        Multiple Admins
                      </span>
                    </li>
                  </ul>

                  {/* CTA button */}
                  <Button
                    className={`mt-6 w-full gap-2 ${
                      isPopular
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : ""
                    }`}
                    variant={isPopular ? "default" : "outline"}
                    onClick={() => handleSubscribe(tier)}
                    disabled={isLoading}
                    aria-label={getCtaAccessibleLabel(tier)}
                  >
                    {getCtaLabel(tier)}
                    {tier !== "free" && <ArrowRight className="h-4 w-4" />}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Feature comparison table for larger screens */}
        <div className="mt-20 hidden lg:block">
          <h2 className="text-2xl font-bold text-center text-foreground mb-8">
            Feature Comparison
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                    Feature
                  </th>
                  {TIERS_ORDER.map((tier) => (
                    <th
                      key={tier}
                      className="px-6 py-4 text-center text-sm font-semibold text-foreground"
                    >
                      {TIER_DISPLAY[tier].name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_LABELS.map((feature, idx) => (
                  <tr
                    key={feature.key}
                    className={idx % 2 === 0 ? "bg-background" : "bg-muted/20"}
                  >
                    <td className="px-6 py-3 text-sm font-medium text-foreground">
                      {feature.label}
                    </td>
                    {TIERS_ORDER.map((tier) => {
                      const value = TIER_FEATURES[tier][feature.key];
                      return (
                        <td key={tier} className="px-6 py-3 text-center text-sm">
                          {typeof value === "boolean" ? (
                            value ? (
                              <Check className="inline-block h-5 w-5 text-green-600" />
                            ) : (
                              <X className="inline-block h-5 w-5 text-muted-foreground/40" />
                            )
                          ) : (
                            <span className="font-medium text-foreground">{value}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
